<template>
  <div class="wrapper">
    <!-- VIP 暂停充值提示 -->
    <div v-if="isNoVipPage" class="novip-page">
      <div class="bg-layer" aria-hidden="true">
        <div class="bg-base"></div>
        <div class="bg-glow bg-glow-1"></div>
        <div class="bg-glow bg-glow-2"></div>
        <div class="bg-glow bg-glow-3"></div>
        <div class="lattice-pattern"></div>
        <div class="cloud-pattern"></div>
        <div class="gold-line gold-line-1"></div>
        <div class="gold-line gold-line-2"></div>
      </div>

      <main class="novip-card">
        <div class="card-corner card-corner-tl"></div>
        <div class="card-corner card-corner-tr"></div>
        <div class="card-corner card-corner-bl"></div>
        <div class="card-corner card-corner-br"></div>

        <div class="novip-icon-wrap" aria-hidden="true">
          <div class="novip-icon-ring"></div>
          <svg class="novip-icon" viewBox="0 0 64 64" fill="none">
            <path d="M13 23L22 32L32 15L42 32L51 23L47 45H17L13 23Z" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round"/>
            <path d="M18 51H46" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
            <circle cx="13" cy="20" r="2.5" fill="currentColor"/>
            <circle cx="32" cy="12" r="2.5" fill="currentColor"/>
            <circle cx="51" cy="20" r="2.5" fill="currentColor"/>
          </svg>
        </div>

        <p class="novip-eyebrow">NOTICE</p>
        <h1 class="novip-title">温馨提示</h1>
        <div class="novip-divider">
          <span></span>
          <i>◆</i>
          <span></span>
        </div>
        <p class="novip-message">现在VIP充值暂未开放</p>
        <p class="novip-submessage">请稍晚再试</p>
        <div class="novip-status">
          <span class="novip-status-dot"></span>
          更多消息咨询大厅客服
        </div>
        <p class="novip-footer">感谢您的理解与耐心等待</p>
      </main>
    </div>

    <!-- 微信环境 -->
    <div v-else-if="isWeChatEnv" class="wechat-guide">
      <div class="bg-layer" aria-hidden="true">
        <div class="bg-base"></div>
        <div class="bg-glow bg-glow-1"></div>
        <div class="bg-glow bg-glow-2"></div>
        <div class="bg-glow bg-glow-3"></div>
        <div class="lattice-pattern"></div>
        <div class="cloud-pattern"></div>
        <div class="gold-line gold-line-1"></div>
        <div class="gold-line gold-line-2"></div>
      </div>

      <div class="wechat-panel">
        <div class="card-corner card-corner-tl"></div>
        <div class="card-corner card-corner-tr"></div>
        <div class="card-corner card-corner-bl"></div>
        <div class="card-corner card-corner-br"></div>

        <div class="wechat-arrow">↗</div>
        <h2 class="wechat-title">微信内无法检测游玩环境</h2>
        <p class="wechat-desc">请点击右上角「 ··· 」菜单</p>
        <p class="wechat-action">选择 <strong>在浏览器中打开</strong></p>
        <div class="wechat-divider"></div>
        <p class="wechat-foot">浏览器可完整检测游玩环境，体验更流畅</p>
      </div>
    </div>

    <!-- 非微信 -->
    <div
      v-else
      class="main-page"
      :class="{ 'tap-screen': phase === 'verify' }"
      @click="phase === 'verify' && onUserConfirm()"
    >
      <div class="bg-layer" aria-hidden="true">
        <div class="bg-base"></div>
        <div class="bg-glow bg-glow-1"></div>
        <div class="bg-glow bg-glow-2"></div>
        <div class="bg-glow bg-glow-3"></div>
        <div class="lattice-pattern"></div>
        <div class="cloud-pattern"></div>
        <div class="gold-line gold-line-1"></div>
        <div class="gold-line gold-line-2"></div>
      </div>

      <div class="page-shell">
        <header class="site-header">
          <span class="header-ornament">◆</span>
          <h1 class="site-title">准备进入</h1>
          <p class="site-tagline">检测浏览器环境 · 匹配最优入口</p>
          <div class="header-divider"></div>
        </header>

        <transition name="fade" mode="out-in">
          <!-- 阶段一：环境检测 -->
          <main v-if="phase === 'matching'" key="matching" class="page-body">
            <p class="phase-label">环境检测中</p>

            <div class="countdown-panel">
              <span class="countdown-num">{{ countdown }}</span>
              <span class="countdown-unit">秒</span>
            </div>

            <div class="progress-bar">
              <div
                class="progress-bar-fill"
                :style="{ width: `${((4 - countdown) / 4) * 100}%` }"
              ></div>
            </div>

            <div class="step-track">
              <div
                v-for="(line, i) in statusLines"
                :key="i"
                class="step-item"
                :class="{ active: statusStep >= i, done: statusStep > i }"
              >
                <span class="step-num">{{ stepNums[i] }}</span>
                <span class="step-text">{{ line }}</span>
              </div>
            </div>

            <p class="phase-hint">正在检查浏览器游玩环境，请稍候…</p>
          </main>

          <!-- 阶段二：确认进入 -->
          <main v-else-if="phase === 'verify'" key="verify" class="page-body page-body-verify">
            <div class="seal-wrap">
              <div class="seal-pulse"></div>
              <div class="seal-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>

            <h2 class="verify-title">环境检测完成</h2>
            <p class="verify-desc">游玩环境已就绪，点击确认进入</p>

            <div class="verify-divider"></div>

            <button class="tap-btn" type="button" @click.stop="onUserConfirm">
              <span class="tap-btn-shine"></span>
              <span class="tap-btn-text">确认进入</span>
            </button>

            <p class="verify-hint">点击屏幕任意位置亦可继续</p>
            <p class="verify-foot">环境检测已完成</p>
          </main>

          <!-- 阶段三：跳转中 -->
          <main v-else key="jumping" class="page-body page-body-jumping">
            <div class="loader-bar">
              <div class="loader-bar-fill"></div>
            </div>

            <h2 class="jumping-title">正在跳转</h2>
            <p class="jumping-desc">请稍候，即将进入游戏</p>

            <div class="loader-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </main>
        </transition>

        <footer class="site-footer">
          <span>推荐使用浏览器游玩</span>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
const S3_DOWNLOAD_URL =
  'https://tyyl-res-437815003327-ap-east-1-an.s3.dualstack.ap-east-1.amazonaws.com/urls-config/download.txt';

export default {
  data() {
    return {
      isNoVipPage: false,
      isWeChatEnv: false,
      phase: 'matching',
      countdown: 4,
      countdownInterval: null,
      statusStep: 0,
      stepNums: ['1', '2', '3'],
      statusLines: ['检测浏览器环境', '检查运行条件', '匹配最优入口'],
      statusInterval: null,
    };
  },
  methods: {
    detectWeChat() {
      const ua = window.navigator.userAgent.toLowerCase();
      this.isWeChatEnv = ua.includes('micromessenger');
      if (!this.isWeChatEnv) {
        this.startMatching();
      }
    },
    startMatching() {
      this.phase = 'matching';
      this.countdown = 4;
      this.statusStep = 0;

      this.statusInterval = setInterval(() => {
        if (this.statusStep < this.statusLines.length - 1) {
          this.statusStep++;
        }
      }, 1100);

      this.countdownInterval = setInterval(() => {
        if (this.countdown > 1) {
          this.countdown--;
        } else {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
          this.finishMatching();
        }
      }, 1000);
    },
    finishMatching() {
      if (this.statusInterval) {
        clearInterval(this.statusInterval);
        this.statusInterval = null;
      }
      this.statusStep = this.statusLines.length;
      this.phase = 'verify';
    },
    onUserConfirm() {
      if (this.phase !== 'verify') return;
      this.phase = 'jumping';
      this.doRedirect();
    },
    doRedirect() {
      fetch(`${S3_DOWNLOAD_URL}?_=${Date.now()}`)
        .then((response) => response.text())
        .then((text) => {
          const urlArray = text
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
          if (urlArray.length === 0) {
            throw new Error('线路列表为空');
          }
          const randomIndex = Math.floor(Math.random() * urlArray.length);
          const target = urlArray[randomIndex].startsWith('http')
            ? urlArray[randomIndex]
            : `https://${urlArray[randomIndex]}`;
          window.location.replace(target);
        })
        .catch((err) => {
          console.error('下载 download.txt 失败:', err);
          this.phase = 'verify';
          alert('线路获取失败，请刷新页面重试');
        });
    },
    clearTimers() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      if (this.statusInterval) {
        clearInterval(this.statusInterval);
        this.statusInterval = null;
      }
    },
  },
  mounted() {
    this.isNoVipPage = window.location.pathname.replace(/\/+$/, '') === '/novip';
    if (this.isNoVipPage) return;
    this.detectWeChat();
  },
  beforeUnmount() {
    this.clearTimers();
  },
};
</script>

<style>
html,
body {
  margin: 0;
  padding: 0;
  font-family: 'PingFang SC', 'STSong', 'SimSun', 'Microsoft YaHei', serif;
  background: #1a0808;
  min-height: 100vh;
  -webkit-tap-highlight-color: transparent;
}

.wrapper {
  min-height: 100vh;
  width: 100vw;
  position: relative;
  overflow: hidden;
}

/* —— 背景 —— */
.bg-layer {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

.bg-base {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 50% at 50% 0%, rgba(120, 20, 20, 0.6) 0%, transparent 70%),
    radial-gradient(ellipse 60% 40% at 50% 100%, rgba(80, 10, 10, 0.5) 0%, transparent 60%),
    linear-gradient(175deg, #2a0a0a 0%, #1a0606 35%, #120404 65%, #1f0808 100%);
}

.bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  animation: glow-pulse 5s ease-in-out infinite;
}

.bg-glow-1 {
  width: 340px;
  height: 340px;
  background: rgba(212, 140, 40, 0.22);
  top: -8%;
  left: 50%;
  transform: translateX(-50%);
  animation-name: glow-pulse-center;
}

.bg-glow-2 {
  width: 260px;
  height: 260px;
  background: rgba(180, 40, 30, 0.18);
  bottom: 10%;
  left: -8%;
  animation-delay: -1.8s;
}

.bg-glow-3 {
  width: 200px;
  height: 200px;
  background: rgba(212, 168, 67, 0.14);
  bottom: 20%;
  right: -5%;
  animation-delay: -3.5s;
}

@keyframes glow-pulse {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.06); }
}

@keyframes glow-pulse-center {
  0%, 100% { opacity: 0.55; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.06); }
}

.lattice-pattern {
  position: absolute;
  inset: 0;
  opacity: 0.07;
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h80v80H0z' fill='none' stroke='%23d4a843' stroke-width='0.8'/%3E%3Cpath d='M0 40h80M40 0v80M0 0l80 80M80 0L0 80' stroke='%23d4a843' stroke-width='0.5'/%3E%3Ccircle cx='40' cy='40' r='12' fill='none' stroke='%23d4a843' stroke-width='0.6'/%3E%3C/svg%3E");
  background-size: 80px 80px;
  mask-image: radial-gradient(ellipse 90% 80% at 50% 50%, black 30%, transparent 100%);
}

.cloud-pattern {
  position: absolute;
  inset: 0;
  opacity: 0.05;
  background-image: url("data:image/svg+xml,%3Csvg width='120' height='60' viewBox='0 0 120 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 35c-8 0-14-6-14-14s6-14 14-14c2 0 4 .5 6 1.3C29 4 34 0 40 0c8 0 14 6 14 14 0 1-.1 2-.3 3 4 2 7 6 7 11 0 7-6 13-13 13H20z' fill='%23d4a843'/%3E%3C/svg%3E");
  background-size: 120px 60px;
}

.gold-line {
  position: absolute;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(212, 168, 67, 0.35), transparent);
}

.gold-line-1 { top: 18%; left: 5%; right: 5%; }
.gold-line-2 { bottom: 22%; left: 10%; right: 10%; opacity: 0.6; }

/* —— 角饰 —— */
.card-corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border-color: #d4a843;
  border-style: solid;
}

.card-corner-tl { top: 6px; left: 6px; border-width: 2px 0 0 2px; }
.card-corner-tr { top: 6px; right: 6px; border-width: 2px 2px 0 0; }
.card-corner-bl { bottom: 6px; left: 6px; border-width: 0 0 2px 2px; }
.card-corner-br { bottom: 6px; right: 6px; border-width: 0 2px 2px 0; }

/* —— VIP 暂停充值提示 —— */
.novip-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  box-sizing: border-box;
}

.novip-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 380px;
  padding: 46px 32px 34px;
  text-align: center;
  box-sizing: border-box;
  background:
    linear-gradient(145deg, rgba(106, 24, 21, 0.82), rgba(39, 7, 7, 0.96)),
    rgba(35, 6, 6, 0.94);
  border: 1px solid rgba(236, 190, 90, 0.48);
  box-shadow:
    0 26px 70px rgba(0, 0, 0, 0.58),
    inset 0 1px 0 rgba(255, 235, 180, 0.08),
    0 0 50px rgba(196, 112, 28, 0.08);
  animation: novip-card-in 0.65s cubic-bezier(0.2, 0.8, 0.25, 1) both;
}

.novip-card::before {
  content: '';
  position: absolute;
  inset: 12px;
  pointer-events: none;
  border: 1px solid rgba(212, 168, 67, 0.1);
}

@keyframes novip-card-in {
  from { opacity: 0; transform: translateY(18px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.novip-icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
  margin: 0 auto 22px;
  color: #f5d78e;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(212, 168, 67, 0.18), rgba(212, 168, 67, 0.03) 65%, transparent 66%);
}

.novip-icon-ring {
  position: absolute;
  inset: 4px;
  border: 1px solid rgba(245, 215, 142, 0.28);
  border-radius: 50%;
  animation: novip-ring 2.8s ease-in-out infinite;
}

@keyframes novip-ring {
  0%, 100% { transform: scale(0.94); opacity: 0.45; }
  50% { transform: scale(1.06); opacity: 1; }
}

.novip-icon {
  width: 52px;
  height: 52px;
  filter: drop-shadow(0 0 10px rgba(245, 215, 142, 0.28));
}

.novip-eyebrow {
  margin: 0 0 8px;
  font-size: 10px;
  color: rgba(245, 215, 142, 0.45);
  letter-spacing: 5px;
}

.novip-title {
  margin: 0;
  font-size: 29px;
  font-weight: 700;
  letter-spacing: 8px;
  text-indent: 8px;
  background: linear-gradient(180deg, #fff8e7, #f5d78e 55%, #c99735);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.novip-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 22px auto 25px;
  color: rgba(212, 168, 67, 0.7);
}

.novip-divider span {
  width: 54px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(212, 168, 67, 0.65));
}

.novip-divider span:last-child {
  transform: rotate(180deg);
}

.novip-divider i {
  font-size: 7px;
  font-style: normal;
}

.novip-message {
  margin: 0;
  font-size: 18px;
  line-height: 1.8;
  color: #f5e6c8;
  letter-spacing: 2px;
}

.novip-submessage {
  margin: 5px 0 0;
  font-size: 15px;
  color: rgba(245, 230, 200, 0.62);
  letter-spacing: 3px;
}

.novip-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
  padding: 8px 14px;
  font-size: 12px;
  color: rgba(245, 215, 142, 0.72);
  letter-spacing: 2px;
  border: 1px solid rgba(212, 168, 67, 0.2);
  background: rgba(212, 168, 67, 0.06);
}

.novip-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e6bd62;
  box-shadow: 0 0 8px rgba(230, 189, 98, 0.8);
  animation: novip-dot 1.8s ease-in-out infinite;
}

@keyframes novip-dot {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}

.novip-footer {
  margin: 26px 0 0;
  font-size: 12px;
  color: rgba(245, 230, 200, 0.3);
  letter-spacing: 2px;
}

/* —— 微信引导 —— */
.wechat-guide {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 20px;
  box-sizing: border-box;
}

.wechat-panel {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 340px;
  padding: 40px 28px 32px;
  text-align: center;
  background: linear-gradient(160deg, rgba(80, 12, 12, 0.9), rgba(35, 6, 6, 0.95));
  border: 1px solid rgba(212, 168, 67, 0.45);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
}

.wechat-arrow {
  font-size: 36px;
  color: #f5d78e;
  margin-bottom: 16px;
  animation: bounce-arrow 1.2s ease-in-out infinite;
}

@keyframes bounce-arrow {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(4px, -6px); }
}

.wechat-title {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 600;
  color: #f5e6c8;
  letter-spacing: 3px;
}

.wechat-desc {
  margin: 0;
  font-size: 15px;
  color: rgba(245, 230, 200, 0.7);
  line-height: 1.8;
}

.wechat-action {
  margin: 8px 0 0;
  font-size: 15px;
  color: rgba(245, 230, 200, 0.85);
}

.wechat-action strong {
  color: #f5d78e;
  letter-spacing: 1px;
}

.wechat-divider {
  width: 60px;
  height: 1px;
  margin: 24px auto;
  background: linear-gradient(90deg, transparent, #d4a843, transparent);
}

.wechat-foot {
  margin: 0;
  font-size: 13px;
  color: rgba(245, 230, 200, 0.4);
  letter-spacing: 1px;
}

/* —— 主页面 —— */
.main-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.main-page.tap-screen {
  cursor: pointer;
}

.page-shell {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  max-width: 440px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
}

/* 顶部品牌区 */
.site-header {
  text-align: center;
  padding: 48px 0 24px;
  flex-shrink: 0;
}

.header-ornament {
  display: block;
  font-size: 14px;
  color: #d4a843;
  margin-bottom: 8px;
  letter-spacing: 8px;
}

.site-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 8px;
  background: linear-gradient(180deg, #fff8e7, #f5d78e 50%, #d4a843);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.site-tagline {
  margin: 10px 0 0;
  font-size: 13px;
  color: rgba(245, 230, 200, 0.5);
  letter-spacing: 3px;
}

.header-divider {
  width: 120px;
  height: 1px;
  margin: 20px auto 0;
  background: linear-gradient(90deg, transparent, rgba(212, 168, 67, 0.6), transparent);
}

/* 主体内容 */
.page-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 0 32px;
}

.phase-label {
  margin: 0 0 20px;
  font-size: 14px;
  color: rgba(245, 215, 142, 0.7);
  letter-spacing: 4px;
}

/* 倒计时 */
.countdown-panel {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  margin-bottom: 24px;
}

.countdown-num {
  font-size: 72px;
  font-weight: 700;
  line-height: 1;
  color: #f5d78e;
  text-shadow: 0 0 30px rgba(245, 215, 142, 0.4);
}

.countdown-unit {
  font-size: 20px;
  color: rgba(245, 215, 142, 0.6);
  letter-spacing: 2px;
}

/* 进度条 */
.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(212, 168, 67, 0.15);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 32px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #b8860b, #f5d78e);
  border-radius: 2px;
  transition: width 0.9s ease;
  box-shadow: 0 0 8px rgba(245, 215, 142, 0.5);
}

/* 步骤条 */
.step-track {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 28px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(212, 168, 67, 0.1);
  color: rgba(245, 230, 200, 0.3);
  transition: all 0.4s ease;
}

.step-item:last-child {
  border-bottom: none;
}

.step-num {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  border: 1px solid rgba(212, 168, 67, 0.25);
  color: rgba(212, 168, 67, 0.4);
  transition: all 0.4s ease;
}

.step-text {
  font-size: 15px;
  letter-spacing: 1px;
}

.step-item.active {
  color: rgba(245, 230, 200, 0.95);
  background: rgba(212, 168, 67, 0.06);
}

.step-item.active .step-num {
  border-color: #f5d78e;
  color: #f5d78e;
  box-shadow: 0 0 12px rgba(245, 215, 142, 0.3);
}

.step-item.done {
  color: rgba(245, 230, 200, 0.5);
}

.step-item.done .step-num {
  border-color: rgba(110, 201, 110, 0.5);
  color: #6ec96e;
  background: rgba(110, 201, 110, 0.1);
}

.phase-hint {
  margin: 0;
  font-size: 13px;
  color: rgba(245, 230, 200, 0.4);
  letter-spacing: 1px;
  text-align: center;
}

/* —— 验证阶段 —— */
.page-body-verify {
  text-align: center;
}

.seal-wrap {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
}

.seal-pulse {
  position: absolute;
  inset: -10px;
  border: 2px solid rgba(212, 168, 67, 0.35);
  border-radius: 50%;
  animation: seal-ring 2s ease-out infinite;
}

@keyframes seal-ring {
  0% { transform: scale(0.9); opacity: 0.8; }
  100% { transform: scale(1.4); opacity: 0; }
}

.seal-icon {
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f5d78e;
  background: linear-gradient(135deg, rgba(212, 168, 67, 0.25), rgba(139, 26, 26, 0.35));
  border: 2px solid rgba(212, 168, 67, 0.45);
  border-radius: 50%;
  box-shadow: 0 0 24px rgba(212, 100, 40, 0.25);
}

.seal-icon svg {
  width: 36px;
  height: 36px;
}

.verify-title {
  margin: 0 0 10px;
  font-size: 26px;
  font-weight: 700;
  color: #f5e6c8;
  letter-spacing: 6px;
}

.verify-desc {
  margin: 0;
  font-size: 15px;
  color: rgba(245, 230, 200, 0.65);
  letter-spacing: 1px;
}

.verify-divider {
  width: 80px;
  height: 1px;
  margin: 28px auto;
  background: linear-gradient(90deg, transparent, #d4a843, transparent);
}

.tap-btn {
  position: relative;
  width: 100%;
  max-width: 280px;
  padding: 16px 32px;
  border: 1px solid rgba(245, 215, 142, 0.45);
  cursor: pointer;
  overflow: hidden;
  background: linear-gradient(180deg, #e8c060, #c49a30 50%, #a07820);
  box-shadow:
    0 6px 24px rgba(180, 120, 20, 0.45),
    0 0 30px rgba(245, 180, 60, 0.15);
  transition: transform 0.15s ease;
}

.tap-btn:active {
  transform: scale(0.97);
}

.tap-btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
  animation: btn-shine 2.5s ease-in-out infinite;
}

@keyframes btn-shine {
  0% { left: -100%; }
  60%, 100% { left: 150%; }
}

.tap-btn-text {
  font-size: 20px;
  font-weight: 700;
  color: #3a1a00;
  letter-spacing: 6px;
}

.verify-hint {
  margin: 20px 0 0;
  font-size: 12px;
  color: rgba(245, 230, 200, 0.35);
  letter-spacing: 1px;
}

.verify-foot {
  margin: 12px 0 0;
  font-size: 11px;
  color: rgba(245, 230, 200, 0.25);
  letter-spacing: 1px;
}

/* —— 跳转中 —— */
.page-body-jumping {
  text-align: center;
}

.loader-bar {
  width: 100%;
  height: 3px;
  background: rgba(212, 168, 67, 0.15);
  margin-bottom: 36px;
  overflow: hidden;
}

.loader-bar-fill {
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, transparent, #f5d78e, transparent);
  animation: loader-slide 1.4s ease-in-out infinite;
}

@keyframes loader-slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}

.jumping-title {
  margin: 0 0 10px;
  font-size: 22px;
  font-weight: 600;
  color: #f5e6c8;
  letter-spacing: 5px;
}

.jumping-desc {
  margin: 0 0 28px;
  font-size: 14px;
  color: rgba(245, 230, 200, 0.55);
  letter-spacing: 1px;
}

.loader-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.loader-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f5d78e;
  animation: dot-bounce 1.2s ease-in-out infinite;
}

.loader-dots span:nth-child(2) { animation-delay: 0.15s; }
.loader-dots span:nth-child(3) { animation-delay: 0.3s; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

/* 底部 */
.site-footer {
  flex-shrink: 0;
  text-align: center;
  padding: 20px 0 32px;
  font-size: 12px;
  color: rgba(245, 230, 200, 0.25);
  letter-spacing: 3px;
}

/* —— 过渡 —— */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(16px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@media (max-width: 499px) {
  .novip-card {
    padding: 40px 24px 30px;
  }

  .novip-title {
    font-size: 26px;
  }

  .site-header {
    padding: 36px 0 20px;
  }

  .site-title {
    font-size: 24px;
    letter-spacing: 6px;
  }

  .countdown-num {
    font-size: 60px;
  }
}
</style>
